import React, { useEffect, useState, createContext } from 'react';
import { ThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Navbar from './components/Navbar';
import WelcomeSlideshow from './components/WelcomeSlideshow';
import Intro from './components/Intro';
import AboutMe from './components/AboutMe';
import Services from './components/Services';
import Portfolio from './components/Portfolio';
import Testimonials from './components/Testimonials';
import FAQ from './components/FAQ';
import Contact from './components/Contact';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import MobileActionBar from './components/MobileActionBar';
import './App.css';
import './styles/css/fonts.css';
import translations from './translations';

export const LanguageContext = createContext();

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#111114',
      light: '#2c2d33',
      dark: '#000000',
      contrastText: '#faf7f1',
    },
    secondary: {
      main: '#c9a35e',
      light: '#e3caa0',
      dark: '#9c7b3f',
      contrastText: '#111114',
    },
    background: {
      default: '#faf7f1',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1a1d',
      secondary: '#6c6c72',
    },
  },
  typography: {
    fontFamily: '"Assistant", "Heebo", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Heebo", "Assistant", sans-serif',
      fontWeight: 700,
      letterSpacing: '0.01em',
    },
    h2: {
      fontFamily: '"Heebo", "Assistant", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.01em',
    },
    h3: {
      fontFamily: '"Heebo", "Assistant", sans-serif',
      fontWeight: 600,
    },
    h4: {
      fontFamily: '"Heebo", "Assistant", sans-serif',
      fontWeight: 600,
    },
    h5: {
      fontFamily: '"Heebo", "Assistant", sans-serif',
      fontWeight: 500,
    },
    button: {
      fontWeight: 700,
      textTransform: 'none',
    },
  },
});

const getLanguageFromPath = () => {
  if (
    typeof window !== 'undefined' &&
    window.location.pathname.split('/').includes('en')
  ) {
    return 'en';
  }
  return 'he';
};

function App() {
  const [isMounted, setIsMounted] = useState(false);
  const [domReady, setDomReady] = useState(false);
  const [language, setLanguage] = useState(getLanguageFromPath());

  useEffect(() => {
    // Mark component as mounted after initial render
    setIsMounted(true);

    // Add a class to indicate JavaScript is enabled
    if (typeof document !== 'undefined') {
      document.documentElement.classList.add('js-enabled');
    }

    // Set a delay to ensure DOM is fully processed
    const timer = setTimeout(() => {
      setDomReady(true);
    }, 200);

    // Listen for path changes to update language
    const handlePathChange = () => {
      setLanguage(getLanguageFromPath());
    };
    window.addEventListener('popstate', handlePathChange);
    window.addEventListener('pushstate', handlePathChange);
    window.addEventListener('replaceState', handlePathChange);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('popstate', handlePathChange);
      window.removeEventListener('pushstate', handlePathChange);
      window.removeEventListener('replaceState', handlePathChange);
    };
  }, []);

  // Show a simple loading state during initial render
  if (!isMounted) {
    return (
      <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LanguageContext.Provider value={{ language, setLanguage, translations }}>
          <div className="App" dir={language === 'he' ? 'rtl' : 'ltr'}>
            {domReady ? (
              <>
                <Navbar />
                <main>
                  <section id="home">
                    <WelcomeSlideshow />
                  </section>
                  <section id="intro">
                    <Intro />
                  </section>
                  <section id="about">
                    <AboutMe />
                  </section>                
                  <section id="portfolio">
                    <Portfolio />
                  </section>
                  <section id="services">
                    <Services />
                  </section>
                  <section id="testimonials">
                    <Testimonials />
                  </section>
                  <section id="faq">
                    <FAQ />
                  </section>
                  <section id="contact">
                    <Contact />
                  </section>
                </main>
                <Footer />
                <ScrollToTop />
                <MobileActionBar />
              </>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div>Preparing your experience...</div>
              </div>
            )}
          </div>
        </LanguageContext.Provider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default App;
